import { z } from "zod";
/**
 * AND-5 — la charge utile des notifications push, décrite une fois pour les deux
 * bords.
 *
 * POURQUOI CE FICHIER EXISTE. Jusqu'ici le `data` d'un push était un
 * `Record<string, any>` côté serveur et un type à champs tous optionnels côté
 * app. Personne ne pouvait dire, sans lire seize fichiers, quels `type` sont
 * réellement émis — et l'audit d'août 2026 a trouvé la conséquence : SIX types
 * émis par l'API n'avaient aucune branche dans l'app, dont la chaîne de routage
 * n'avait même pas d'`else`. Taper la notification ouvrait l'accueil, sans
 * erreur : le pire mode de panne, celui qui ressemble à un succès.
 *
 * LE PIÈGE PROPRE AU BINAIRE PUBLIÉ. Sur le web on redéploie et l'ancien code
 * disparaît en minutes. Un binaire de store vit des MOIS chez l'utilisateur :
 * le jour où l'API émet un `type` neuf, les téléphones déjà installés ne
 * l'apprendront jamais. « On ajoutera les types au fil de l'eau » ne marche donc
 * pas — c'est la rustine que ce fichier existe pour rendre inutile.
 *
 * LA RÉPONSE : chaque membre porte, EN PLUS de son `type`, une destination de
 * REPLI prise dans un jeu volontairement minuscule et figé à vie, que tout
 * binaire connaît déjà parce qu'il l'a compilé le jour de sa publication. Le
 * serveur pose ce champ dans le payload (`apps/api/src/services/push.ts`), donc
 * même une app qui ignore le `type` sait où aller.
 */
// ── Destinations de repli ────────────────────────────────────────────────────
/**
 * Jeu STABLE et MINIMAL de destinations de repli. Quatre valeurs, choisies
 * parce qu'elles existent dans l'app depuis toujours et qu'aucune évolution
 * produit ne les supprimera : les onglets et les deux conversations.
 *
 * ⚠️ CETTE ÉNUMÉRATION NE S'ÉTEND PAS. Ajouter une cinquième valeur la rendrait
 * inconnue de tous les binaires déjà publiés — c'est-à-dire exactement le
 * problème que le repli est censé résoudre. Un besoin neuf s'exprime par un
 * nouveau membre de `PushDataSchema` qui RETOMBE sur l'une de ces quatre.
 */
export const PUSH_FALLBACK_ROUTES = ["home", "account", "session", "group"];
export const PushFallbackRouteSchema = z.enum(PUSH_FALLBACK_ROUTES);
// ── Canaux Android ───────────────────────────────────────────────────────────
/**
 * Identifiants des canaux de notification Android (décision D6). L'identifiant
 * est TECHNIQUE et figé À VIE : Android mémorise le canal côté système, et un
 * identifiant renommé après publication crée un canal neuf tout en laissant
 * l'ancien — donc un doublon dans les réglages de l'utilisateur, et ses
 * préférences perdues. Le NOM affiché, lui, est traduit côté app et reste
 * modifiable.
 *
 * `default` est conservé à vie : c'est là que retombent les notifications
 * envoyées SANS `channelId` — le seul comportement sûr face à un client dont on
 * ne sait pas s'il a créé les canaux.
 */
export const PUSH_CHANNEL_IDS = [
    "default",
    "messages",
    "social",
    "games",
    "bookings",
];
export const PushChannelIdSchema = z.enum(PUSH_CHANNEL_IDS);
// ── L'union discriminée ──────────────────────────────────────────────────────
/**
 * Fabrique un membre de l'union. Le `fallbackRoute` a une VALEUR PAR DÉFAUT :
 * un payload émis par une instance N-1 (rolling deploy) ne le porte pas encore,
 * et il ne doit pas pour autant échouer au parsing — il retrouve simplement sa
 * destination depuis le schéma local.
 */
function membre(type, fallbackRoute, champs) {
    return z.object({
        type: z.literal(type),
        fallbackRoute: PushFallbackRouteSchema.default(fallbackRoute),
        ...champs,
    });
}
/** Un identifiant d'objet du domaine : présent la plupart du temps, jamais garanti. */
const id = () => z.string().min(1).optional();
/**
 * TOUT `data.type` émis par l'API, un membre par type. La liste est gardée par
 * `apps/api/tests/push-channels.test.ts`, qui relit les fichiers émetteurs :
 * ajouter un `sendNotification` avec un `type` absent d'ici fait ROUGIR le gate.
 */
export const PushDataSchema = z.discriminatedUnion("type", [
    // — Messagerie —
    membre("message", "session", { sessionId: id() }),
    membre("group_message", "group", { groupId: id() }),
    membre("group_added", "group", { groupId: id() }),
    // — Amis & invitations —
    membre("friend_request", "account", { requestId: id() }),
    membre("friend_request_accepted", "home", { userId: id() }),
    // — Jeux —
    membre("game_challenge", "session", { matchId: id(), sessionId: id() }),
    membre("game_turn", "session", { matchId: id(), sessionId: id() }),
    membre("game_result", "session", { matchId: id(), sessionId: id() }),
    // — Rendez-vous, commandes, commerce —
    membre("booking_pro", "account", { bookingId: id(), sessionId: id() }),
    membre("booking_client", "account", { bookingId: id(), sessionId: id() }),
    membre("post_visit", "account", {
        bookingId: id(),
        reviewUrl: z.string().optional(),
        rebookServiceId: id(),
        rebookStartAt: z.string().optional(),
        proHandle: id(),
    }),
    membre("order_pro", "account", { orderId: id() }),
    membre("order_client", "account", { orderId: id() }),
    membre("loyalty", "account", { businessId: id() }),
    membre("loyalty_pro", "account", { businessId: id() }),
    membre("broadcast_offer", "account", { broadcastId: id() }),
    // — Compte & modération : rien à ouvrir de plus précis que le Compte —
    membre("report_status", "account", { reportId: id() }),
    membre("pro_verification", "account", {}),
    membre("broadcast_suspension", "account", {}),
]);
/** Tous les `type` connus, dans l'ordre de déclaration. */
export const PUSH_DATA_TYPES = PushDataSchema.options.map((o) => o.shape.type.value);
// ── Les deux tables dérivées ─────────────────────────────────────────────────
/**
 * `type` → destination de repli. Extraite du schéma plutôt que recopiée : une
 * seconde liste finirait par diverger de la première, et c'est justement la
 * divergence qui a produit les six types orphelins.
 */
export const PUSH_FALLBACK_BY_TYPE = Object.fromEntries(PushDataSchema.options.map((o) => [
    o.shape.type.value,
    // Le défaut du champ EST la destination de repli du membre.
    o.shape.fallbackRoute._def.defaultValue(),
]));
/**
 * `type` → canal Android (décision D6, quatre canaux nommés + `default`).
 *
 * Écrite à la main, et c'est voulu : le canal est une décision de VIE PRIVÉE et
 * de confort (l'utilisateur coupe *Jeux* sans couper *Messages*, et règle la
 * visibilité sur écran verrouillé canal par canal), pas une propriété technique
 * du type. Le `Record` exhaustif fait rougir le typecheck si un membre neuf
 * n'est pas classé.
 */
export const PUSH_CHANNEL_BY_TYPE = {
    message: "messages",
    group_message: "messages",
    group_added: "messages",
    friend_request: "social",
    friend_request_accepted: "social",
    game_challenge: "games",
    game_turn: "games",
    game_result: "games",
    booking_pro: "bookings",
    booking_client: "bookings",
    post_visit: "bookings",
    order_pro: "bookings",
    order_client: "bookings",
    loyalty: "bookings",
    loyalty_pro: "bookings",
    broadcast_offer: "bookings",
    // Administratif : pas de canal dédié — quatre canaux, pas huit. Un canal de
    // plus est une ligne de plus dans les réglages Android, donc une chance de
    // plus que l'utilisateur coupe tout d'un geste.
    report_status: "default",
    pro_verification: "default",
    broadcast_suspension: "default",
};
// ── Le repli, côté app ───────────────────────────────────────────────────────
/**
 * Destination de repli d'un payload, y compris d'un `type` INCONNU.
 *
 * Ordre de lecture, non négociable :
 *   1. `fallbackRoute` présent dans le payload — c'est le serveur qui parle, et
 *      lui connaît le type ; c'est le seul chemin qui sauve un binaire figé ;
 *   2. la table locale, si le binaire connaît quand même le type ;
 *   3. `home` — mieux vaut l'accueil qu'un tap sans effet.
 *
 * Volontairement tolérante (`unknown` en entrée, jamais d'exception) : elle est
 * appelée sur une charge utile qui vient du réseau, dans un chemin où lever
 * ferait perdre le tap au lieu de le dégrader.
 */
export function pushFallbackRoute(data) {
    if (typeof data !== "object" || data === null)
        return "home";
    const brut = data.fallbackRoute;
    const depuisLeServeur = PushFallbackRouteSchema.safeParse(brut);
    if (depuisLeServeur.success)
        return depuisLeServeur.data;
    const type = data.type;
    if (typeof type === "string" && type in PUSH_FALLBACK_BY_TYPE) {
        return PUSH_FALLBACK_BY_TYPE[type];
    }
    return "home";
}
// ── Le garde-fou de version, côté serveur ────────────────────────────────────
/**
 * Version d'app à partir de laquelle un client a CRÉÉ les canaux nommés.
 *
 * ⚠️ L'INVARIANT QUI TIENT TOUT CE FICHIER. La spec initiale pariait qu'un
 * client ne connaissant pas un `channelId` retomberait sur `default`. Ce repli
 * N'EXISTE PAS côté Android : une notification qui désigne un canal absent est
 * silencieusement JETÉE. Envoyer un `channelId` à un binaire dont on ne sait
 * pas s'il a créé le canal, c'est donc supprimer sa notification.
 *
 * D'où la règle : on n'envoie un `channelId` QUE si `DeviceToken.appVersion`
 * (AND-BC5) prouve que le binaire est au moins cette version. Version inconnue
 * (`null` — tous les tokens enregistrés avant AND-BC5), non comparable, ou
 * antérieure ⇒ AUCUN `channelId`, et la notification retombe sur le canal par
 * défaut d'Expo. C'est toujours sûr, au prix d'un canal moins fin.
 *
 * ⚠️ VALEUR À TENIR AVEC `app.json`. Le premier build Android publié doit porter
 * un `expo.version` >= cette constante. Tant qu'`app.json` reste en `1.0.0`,
 * AUCUN client ne reçoit de `channelId` — ce qui est le comportement voulu :
 * les canaux existent déjà côté app, mais rien ne dépend d'eux.
 */
/**
 * ⚠️ AFFIRMATION RÉFUTÉE — À NE PAS RÉINTRODUIRE.
 *
 * La spec d'AND-5, et ce fichier avant sa relecture, posaient qu'Android JETTE
 * une notification désignant un canal absent de l'appareil — d'où un garde-fou
 * qui n'envoyait de `channelId` qu'aux versions clientes assez récentes.
 *
 * **C'est faux, et la preuve était dans `node_modules`, pas sur un appareil** :
 * `expo-notifications/android/.../BaseNotificationBuilder.kt` journalise
 * « Channel '%s' doesn't exists. Fallback to '%s' channel » puis **délivre**
 * sur son canal de repli. Rien n'est perdu.
 *
 * Deux conséquences, dans ce sens-là :
 *  1. On envoie TOUJOURS le `channelId` : un client ancien reçoit quand même sa
 *     notification, un client récent la reçoit dans le bon canal.
 *  2. Ne PAS l'envoyer est le mauvais choix — la notification part alors sur le
 *     canal de repli d'Expo, affiché « Miscellaneous » dans les réglages du
 *     téléphone, et pas sur notre canal `default`.
 *
 * Le garde-fou par version est donc retiré : il ne protégeait de rien, et il
 * rendait l'US inopérante (`expo.version` valant `1.0.0`, aucun `channelId` ne
 * serait jamais parti).
 */
export const MIN_APP_VERSION_CANAUX = "1.1.0";
/**
 * Comparaison de versions sémantiques tolérante : on ne compare que les trois
 * premiers segments numériques, et TOUT ce qui n'est pas comparable rend
 * `false`. Un pré-release (`1.1.0-beta`) compare sur `1.1.0` : il embarque le
 * code des canaux, donc il les a créés.
 */
export function clientSupportsChannels(appVersion, minimum = MIN_APP_VERSION_CANAUX) {
    const decouper = (v) => {
        const m = /^(\d+)(?:\.(\d+))?(?:\.(\d+))?/.exec(v.trim());
        if (!m)
            return null;
        return [Number(m[1]), Number(m[2] ?? 0), Number(m[3] ?? 0)];
    };
    if (typeof appVersion !== "string" || appVersion.length === 0)
        return false;
    const a = decouper(appVersion);
    const b = decouper(minimum);
    if (!a || !b)
        return false;
    for (let i = 0; i < 3; i += 1) {
        if (a[i] > b[i])
            return true;
        if (a[i] < b[i])
            return false;
    }
    return true;
}
/**
 * Le `channelId` à poser sur un message Expo — ou `undefined`, qui est TOUJOURS
 * la réponse sûre. Trois conditions cumulatives : la plateforme est Android
 * (iOS et web n'ont pas de canaux), la version du binaire est connue et
 * suffisante, et le `type` est classé.
 */
export function pushChannelFor(params) {
    if (params.platform !== "android")
        return undefined;
    const t = params.type;
    if (typeof t !== "string" || !(t in PUSH_CHANNEL_BY_TYPE))
        return undefined;
    return PUSH_CHANNEL_BY_TYPE[t];
}
//# sourceMappingURL=push.js.map